// Curated icon set for registry entries. A registry entry stores an `icon` NAME (a stable string,
// since the backend cannot store a React component); this map resolves that name to the actual MUI
// icon at render time. The set is intentionally small: the icons the Studio tools already use in the
// flag-off rail, plus a few generic ones, so a flag-on Studio tool renders the SAME icon it shows
// with the flag off. Extend the map (and CURATED_ICON_NAMES) to offer more choices later.
//
// resolveIcon() falls back to a caller-supplied default (the per-kind icon) when the name is missing
// or unknown, so an entry with no icon behaves exactly as before this feature.

import type { ReactElement } from 'react'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined'
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined'
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined'
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'
import HearingOutlinedIcon from '@mui/icons-material/HearingOutlined'
import BuildIcon from '@mui/icons-material/Build'
import PublicIcon from '@mui/icons-material/Public'
import ExtensionIcon from '@mui/icons-material/Extension'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'

type IconRenderer = (props: { fontSize?: 'small' | 'medium' | 'large' }) => ReactElement

// name -> renderer. Names are the seed's stable identifiers; keep them short and descriptive.
const ICON_REGISTRY: Record<string, IconRenderer> = {
  assignment: (p) => <AssignmentOutlinedIcon fontSize={p.fontSize} />, // Survey Guard
  note: (p) => <NoteAltOutlinedIcon fontSize={p.fontSize} />,          // Interview Notetaker
  mic: (p) => <MicNoneOutlinedIcon fontSize={p.fontSize} />,           // Transcription Assistant
  chat: (p) => <ChatOutlinedIcon fontSize={p.fontSize} />,             // ATR Generator
  table: (p) => <TableChartOutlinedIcon fontSize={p.fontSize} />,      // Tab Plan Generator
  apps: (p) => <AppsOutlinedIcon fontSize={p.fontSize} />,             // Community Apps / generic app
  palette: (p) => <PaletteOutlinedIcon fontSize={p.fontSize} />,       // Visual Semiotics
  hearing: (p) => <HearingOutlinedIcon fontSize={p.fontSize} />,       // AI Listening
  build: (p) => <BuildIcon fontSize={p.fontSize} />,                   // generic tool (per-kind default)
  public: (p) => <PublicIcon fontSize={p.fontSize} />,                 // public app
  extension: (p) => <ExtensionIcon fontSize={p.fontSize} />,           // skills-like / extension
  folder: (p) => <FolderOutlinedIcon fontSize={p.fontSize} />,         // files / storage
  insights: (p) => <InsightsOutlinedIcon fontSize={p.fontSize} />,     // analytics / insights
}

/** The names offered by the icon picker, with a human label for each (order preserved). */
export const CURATED_ICONS: { name: string; label: string }[] = [
  { name: 'assignment', label: 'Assignment' },
  { name: 'note', label: 'Notes' },
  { name: 'mic', label: 'Microphone' },
  { name: 'chat', label: 'Chat' },
  { name: 'table', label: 'Table' },
  { name: 'apps', label: 'Apps' },
  { name: 'palette', label: 'Palette' },
  { name: 'hearing', label: 'Hearing' },
  { name: 'insights', label: 'Insights' },
  { name: 'folder', label: 'Folder' },
  { name: 'extension', label: 'Extension' },
  { name: 'build', label: 'Tool' },
  { name: 'public', label: 'Public' },
]

export const CURATED_ICON_NAMES = new Set(CURATED_ICONS.map((i) => i.name))

/**
 * Resolve an entry's stored icon name to a rendered MUI icon. Returns `fallback` when the name is
 * empty, null, or not in the curated set, so an entry with no icon renders exactly as before.
 */
export function resolveIcon(
  name: string | null | undefined,
  fallback: ReactElement,
  fontSize: 'small' | 'medium' | 'large' = 'small',
): ReactElement {
  if (name && ICON_REGISTRY[name]) return ICON_REGISTRY[name]({ fontSize })
  return fallback
}
